import { Banner, Button, Inline, Switch } from '@ui';
import { useEffect, useState } from 'react';
import { bugApi, type ProjectFeatureSet, type ProjectVersion, type TrackerVersion, type WidgetSeen } from '../bugApi';
import { OLD_MARK, VersionMarks, reason } from './TrackerVersions';

// «Проект» → «Возможности» (Мн8, Вк1: only the working project's data — the catalog of tracker builds is «Трекер → Версии»): one switch per feature, showing how it works on the site now — the saved set, else what the
// site's widget reported (facts models widget_features, features_screen; requirements 218–223). «Сохранить» writes the
// whole set, so saving without a change keeps the site as it is (П330).

interface Item {
  key: string;
  label: string;
  /** The site must give it (a page, a link); off the hook the item stays grey on the site. */
  hook?: 'news' | 'tracker';
}

const MAIN: { title: string; items: Item[] }[] = [
  {
    title: 'Кнопка помощи',
    items: [
      { key: 'bug', label: 'Баг-репорт' },
      { key: 'ux', label: 'Последи за мной' },
      { key: 'ask', label: 'Задать вопрос' },
      { key: 'news', label: 'Что нового', hook: 'news' },
      { key: 'mask', label: 'Скрыть данные людей' },
      { key: 'tracker', label: 'Ссылка на трекер', hook: 'tracker' },
    ],
  },
  {
    title: 'Окно репорта',
    items: [
      { key: 'files', label: 'Файлы' },
    ],
  },
];
const ALL = MAIN.flatMap((group) => group.items);
const HOOK_MISSING = { news: 'нет экрана «Что нового» на сайте', tracker: 'нет ссылки на сайте' } as const;
/** Хс1: without the site's screen the switch cannot be turned on (left on before the screen went — can be turned off). */
const LOCKED_WITHOUT_HOOK = new Set(['news']);
/** The radio value of «no choice»: the project follows each new release (tracker_versions Вр1). It is the row of the latest version (Вр6). */
const AUTO = 'auto';

export function ProjectFeatures({ project, onClose }: { project: string | null; onClose?: () => void }) {
  const [draft, setDraft] = useState<ProjectFeatureSet | null>(null);
  const [seen, setSeen] = useState<WidgetSeen | null>(null);
  const [known, setKnown] = useState(true);
  /** Т1: switches the person changed — while the site's state is unknown only these are saved. */
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<TrackerVersion[] | null>(null);
  const [pv, setPv] = useState<ProjectVersion | null>(null);
  const [choice, setChoice] = useState<string>(AUTO);
  const [versionError, setVersionError] = useState('');
  const [saved, setSaved] = useState(false);
  /** Кн4: «Контроль агента» — a hard refusal of the gates for the agent's token; saved at once, on by default (Жк1). */
  const [agentHard, setAgentHard] = useState(true);
  const latestVersion = versions?.find((v) => v.latest)?.version ?? null;

  useEffect(() => {
    if (!project) return;
    setDraft(null);
    setTouched(new Set());
    setError('');
    setSaved(false);
    setVersionError('');
    setVersions(null);
    bugApi
      .trackerVersions()
      .then((res) => setVersions(res.versions))
      .catch((err) => setVersionError(reason(err, 'Версии не загрузились — повторите.')));
    bugApi
      .projectFeatures(project)
      .then((res) => {
        setPv(res.version ?? null);
        setAgentHard(res.agentControl?.hard !== false);
        setChoice(res.version?.chosen ?? AUTO);
        // П329: the saved set wins; what it leaves out — as the site works now.
        const live: ProjectFeatureSet = { ...(res.seen?.features ?? {}), ...res.features };
        setSeen(res.seen);
        // Known: the widget reported itself, or every switch is saved (features_save.known).
        setKnown(!!res.seen || ALL.every((item) => item.key in res.features));
        // Мд1: no saved value and nothing reported by the site — on, not greyed out.
        setDraft(Object.fromEntries(ALL.map((item) => [item.key, live[item.key] ?? true])));
      })
      .catch(() => setError('Не удалось загрузить — повторите.'));
  }, [project]);

  const save = async () => {
    if (!project || !draft) return;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      // Т1: what the person didn't touch stays the site's while its state is unknown.
      const send = known ? draft : Object.fromEntries(Object.entries(draft).filter(([key]) => touched.has(key)));
      await bugApi.saveProjectFeatures(project, send);
    } catch {
      setError('Не удалось сохранить — повторите.');
      setBusy(false);
      return;
    }
    try {
      // Вр6: the latest version is «no choice».
      const next = choice === AUTO || choice === latestVersion ? null : choice;
      if (next !== (pv?.chosen ?? null)) setPv((await bugApi.chooseTrackerVersion(project, next)).version);
      setSaved(true);
      onClose?.();
    } catch (err) {
      // Вр3: the service names why (an old or unreleased version).
      setVersionError(reason(err, 'Версию не удалось выбрать — повторите.'));
    } finally {
      setBusy(false);
    }
  };

  const row = (item: Item) => {
    const value = draft?.[item.key];
    const missing = item.hook && seen && !seen.hooks[item.hook] ? HOOK_MISSING[item.hook] : undefined;
    const unknown = !known && !touched.has(item.key) ? 'как на сайте — неизвестно' : undefined;
    const checked = value === true || value === 'default-on';
    return (
      <div key={item.key} className="bug-feature">
        <Switch
          label={item.label}
          labelFirst
          checked={checked}
          disabled={!!missing && !checked && LOCKED_WITHOUT_HOOK.has(item.key)}
          // The screen mode kept «at every load» while on; off → off.
          onChange={(on) => {
            setTouched((prev) => new Set(prev).add(item.key));
            setDraft((prev) => ({ ...prev, [item.key]: on ? (item.key === 'mask' && value === 'default-on' ? 'default-on' : true) : false }));
          }}
        />
        {(missing || unknown) && <span className="bug-feature-hint">{missing ?? unknown}</span>}
      </div>
    );
  };

  // Вк3: the project picks only among versions that can be picked; the one it is pinned to stays in sight (disabled when it went old).
  const pickable = (v: TrackerVersion) => v.state === 'active' || choice === v.version;
  // Each version once, without the other projects' counts: this screen is the working project's (Вк1).
  const versionRow = (v: TrackerVersion) => (
    <div key={v.version} className="bug-version-row" data-version={v.version}>
      <label className="bug-version-pick-label" title={v.state !== 'active' ? `${OLD_MARK[v.state].label}: заново не выбрать` : undefined}>
        <input
          type="radio"
          className="jui-radio"
          name="tracker-version"
          value={v.latest ? AUTO : v.version}
          checked={v.latest ? choice === AUTO || choice === v.version : choice === v.version}
          disabled={v.state !== 'active'}
          onChange={() => setChoice(v.latest ? AUTO : v.version)}
        />
        <span className="bug-version-label">
          <span className="mono">{v.version}</span>
          <VersionMarks v={v} />
        </span>
      </label>
    </div>
  );

  const loadedOld = pv?.loaded && (pv.loaded.state === 'deprecated' || pv.loaded.state === 'doomed') ? pv.loaded : null;
  const versionSection = versions && (
    <section className="bug-features-block bug-versions">
      <h3 className="bug-features-title">Версия сайта</h3>
      {loadedOld && (
        <Banner tone="warning" title={`Сайт на версии ${loadedOld.version}: ${loadedOld.state === 'doomed' ? 'к удалению' : 'устарела'}`}>
          Выберите другую
        </Banner>
      )}
      {pv?.loaded && !loadedOld && <div className="bug-feature-hint">{`Сайт загружает ${pv.loaded.version}`}</div>}
      <div className="bug-versions-list" role="radiogroup" aria-label="Версия сайта">
        {!latestVersion && (
          <div className="bug-version-pick">
            <label className="bug-version-pick-label">
              <input type="radio" className="jui-radio" name="tracker-version" value={AUTO} checked={choice === AUTO} onChange={() => setChoice(AUTO)} />
              <span>Последняя (автоматически)</span>
            </label>
          </div>
        )}
        {versions.filter((v) => v.latest || pickable(v)).map(versionRow)}
      </div>
    </section>
  );

  return (
    <section className="bug-features-panel" aria-label={`Возможности · ${project ?? ''}`}>
      {error && <Banner tone="error" title={error} />}
      {versionError && <Banner tone="error" title="Отказ" className="bug-version-error">{versionError}</Banner>}
      {saved && <Banner tone="success" title="Сохранено" />}
      {draft && !known && <Banner tone="info" title="Сайт ещё не открывал виджет — как он работает сейчас, неизвестно. Сохранятся только переключатели, которые вы тронете." />}
      {draft && (
        <div className="bug-features">
          <div className="bug-features-block">
            {MAIN.map((group) => (
              <section key={group.title} className="bug-features-group">
                <h3 className="bug-features-title">{group.title}</h3>
                {group.items.map(row)}
              </section>
            ))}
            <section className="bug-features-group" data-testid="agent-control-switch">
              <h3 className="bug-features-title">Контроль агента</h3>
              <div className="bug-control-switch">
                <Switch
                  label="Шлюзы жёстко"
                  labelFirst
                  checked={agentHard}
                  onChange={(on) => {
                    if (!project) return;
                    setAgentHard(on);
                    bugApi.saveAgentControl(project, on).catch(() => {
                      setAgentHard(!on);
                      setError('Не удалось сохранить — повторите.');
                    });
                  }}
                />
                <span className="bug-feature-hint">{agentHard ? 'включено по умолчанию: агент без полей заметки не пройдёт' : 'выключено: только подсказки и предупреждения'}</span>
              </div>
            </section>
          </div>
          {versionSection}
        </div>
      )}
      <Inline gap={2} justify="end">
        <Button variant="primary" busy={busy} disabled={!draft} onClick={() => void save()}>
          Сохранить
        </Button>
        {onClose && (
          <Button variant="text" onClick={onClose}>
            Отмена
          </Button>
        )}
      </Inline>
    </section>
  );
}
